import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import axios from 'helpers/axios';
import config from 'helpers/config';

import RepairOrder from 'models/RepairOrderModel';
import Service from 'models/ServiceModel';
import actionTypes from 'helpers/actionTypes';
import Payments from './Payments';
import RepairOrderActivityLog from './RepairOrderActivityLog';
import { getActivityLog } from 'actions/repairOrderActions';
import RepairOrderStatus from './RepairOrderStatus';
import RepairOrderAuthorizations from './RepairOrderAuthorizations';
import { resolveUserName } from 'helpers/userNameResolver';
import User from 'models/UserModel';
import { Check, Clock, CreditCard, FileText } from 'lucide-react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import RepairOrderDetails from './RepairOrderDetails';

interface Props {
    repairOrder: RepairOrder;
    services: Service[];
    dispatch: Dispatch;
}

interface State {
    collapsedStates: boolean[];
    createdByUserFullName: string;

    isDueDatePickerOpen: boolean;
    users: User[];
    isServiceWriterPickerOpen: boolean;
}

class RepairOrderSideView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            collapsedStates: [false, false, false, false],
            createdByUserFullName: '',
            isDueDatePickerOpen: false,
            isServiceWriterPickerOpen: false,
            users: [],
        };
        this.updateOrderStatus = this.updateOrderStatus.bind(this);
        this.updateWorkflowStatus = this.updateWorkflowStatus.bind(this);
        this.updateDueDate = this.updateDueDate.bind(this);
    }

    componentDidMount() {
        setTimeout(() => {
            resolveUserName(this.props?.repairOrder?.createdById!)
                .then((createdByUserFullName) => this.setState({ createdByUserFullName }))
                .catch((_) => _);
        }, 2000);

        this.getUsers();
    }

    getUsers() {
        axios.get(`${config.apiUrl}/user`).then((res) => {
            const users = res.data?.map((user: any) => {
                return { ...user, name: `${user.firstName} ${user.lastName}` };
            });
            this.setState({ users });
        });
    }

    updateOrderStatus(status: number) {
        if (status !== this.props.repairOrder.status) {
            axios
                .put(`${config.apiUrl}/repairOrder`, { ...this.props.repairOrder, status })
                .then((_) => {
                    this.props.dispatch({
                        type: actionTypes.GET_REPAIR_ORDER_SUCCESS,
                        payload: { ...this.props.repairOrder, status },
                    });
                    this.props.dispatch<any>(getActivityLog(this.props.repairOrder.id));
                })
                .catch((err) => console.log(err));
        }
    }

    updateWorkflowStatus(workflowStatusId: string) {
        if (workflowStatusId !== this.props.repairOrder.workflowStatusId) {
            axios
                .put(`${config.apiUrl}/repairOrder`, { ...this.props.repairOrder, workflowStatusId })
                .then((_) => {
                    this.props.dispatch({
                        type: actionTypes.GET_REPAIR_ORDER_SUCCESS,
                        payload: { ...this.props.repairOrder, workflowStatusId },
                    });
                    this.props.dispatch<any>(getActivityLog(this.props.repairOrder.id));
                })
                .catch((err) => console.log(err));
        }
    }

    updateDueDate(dueDate: string) {
        this.setState({ isDueDatePickerOpen: false });
        axios
            .put(`${config.apiUrl}/repairOrder`, { ...this.props.repairOrder, dueDate })
            .then((_) => {
                this.props.dispatch({
                    type: actionTypes.GET_REPAIR_ORDER_SUCCESS,
                    payload: { ...this.props.repairOrder, dueDate },
                });
                this.props.dispatch<any>(getActivityLog(this.props.repairOrder.id));
            })
            .catch((err) => console.log(err));
    }

    updateServiceWriter(serviceWriterId: string) {
        this.setState({ isServiceWriterPickerOpen: false });
        axios
            .put(`${config.apiUrl}/repairOrder`, { ...this.props.repairOrder, serviceWriterId })
            .then((_) => {
                this.props.dispatch({
                    type: actionTypes.GET_REPAIR_ORDER_SUCCESS,
                    payload: { ...this.props.repairOrder, serviceWriterId },
                });
                this.props.dispatch<any>(getActivityLog(this.props.repairOrder.id));
            })
            .catch((err) => console.log(err));
    }

    getSidebarComponents() {
        return [
            {
                title: (
                    <span className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <Check className="w-5 h-5 text-green-500" />
                        <span>Authorizations</span>
                    </span>
                ),

                component: (
                    <RepairOrderAuthorizations
                        services={this.props.services}
                        repairOrderId={this.props.repairOrder.id}
                    />
                ),
                isCollapsed: this.state.collapsedStates[0],
            },
            {
                title: (
                    <span className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <CreditCard className="w-5 h-5 text-blue-500" />
                        <span>Payments</span>
                    </span>
                ),
                component: <Payments repairOrder={this.props.repairOrder} services={this.props.services} />,
                isCollapsed: this.state.collapsedStates[1],
            },
            {
                title: (
                    <span className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <FileText className="w-5 h-5 text-purple-500" />
                        <span>RO Status</span>
                    </span>
                ),
                component: (
                    <RepairOrderStatus
                        status={this.props.repairOrder?.status}
                        workflowStatusId={this.props.repairOrder?.workflowStatusId!}
                        updateOrderStatus={this.updateOrderStatus}
                        updateWorkflowStatus={this.updateWorkflowStatus}
                    />
                ),
                isCollapsed: this.state.collapsedStates[2],
            },
            {
                title: (
                    <span className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <span>Activity Log</span>
                    </span>
                ),
                component: <RepairOrderActivityLog />,
                isCollapsed: this.state.collapsedStates[3],
            },
        ];
    }

    render() {
        const { repairOrder } = this.props;
        return (
            <div className="my-4 ml-4 w-1/4 h-fit">
                <RepairOrderDetails
                    repairOrder={repairOrder}
                    updateDueDate={this.updateDueDate}
                    updateServiceWriter={this.updateServiceWriter}
                />

                {!!repairOrder &&
                    this.getSidebarComponents().map((component, index) => {
                        return (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm bg-white"
                            >
                                <div
                                    className="flex justify-between items-center p-3 bg-white cursor-pointer hover:bg-gray-50"
                                    onClick={() => {
                                        const updatedCollapsedStates = this.state.collapsedStates;
                                        updatedCollapsedStates[index] = !updatedCollapsedStates[index];
                                        this.setState({ collapsedStates: updatedCollapsedStates });
                                    }}
                                >
                                    <div className="flex items-center space-x-2 font-medium text-gray-700">
                                        {component.title}
                                    </div>

                                    <MdKeyboardArrowDown
                                        className={`transition-transform text-gray-500 hover:bg-blue-100 p-0.5 rounded-full ${
                                            component.isCollapsed ? 'rotate-180' : ''
                                        }`}
                                        size={20}
                                    />
                                </div>

                                {
                                    <div
                                        className={` bg-white border-t border-gray-100 transition-all duration-500  overflow-x-visible ${
                                            component.isCollapsed
                                                ? 'max-h-0 overflow-y-hidden'
                                                : 'h-fit overflow-y-visible'
                                        }`}
                                    >
                                        <div className="p-4">{component.component}</div>
                                    </div>
                                }
                            </div>
                        );
                    })}
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    services: state.repairOrder.services,
    repairOrder: state.repairOrder.repairOrder,
});

export default connect(mapStateToProps)(RepairOrderSideView);
